import type { Subscriptions } from '@ps/models/lib/creator-subscription';
import {
  PopStoreSubscriptionLevel,
  PopStoreSubscriptionStatus,
} from '@ps/models/lib/creator-subscription';

/**
 * Minimum sellable price (in dollars) for a tier. 1:1 with
 * legacy `getMinimumSubscriptionPrice` —
 * `flows/bio/pages/subscription-configuration/functions/...`.
 *
 * Rules (all values stored server-side in cents, returned here
 * in whole dollars):
 *   - SUBSCRIBER → 50¢ floor.
 *   - FAN        → `max(SUBSCRIBER + 10¢, 60¢)`.
 *   - VIP        → `max(FAN + 10¢, 70¢)`.
 *
 * The "max" rules enforce price-monotonicity: a higher tier
 * must cost at least 10¢ more than the tier below it.
 */
const DEFAULT_SUBSCRIBER_PRICE = 50;
const DEFAULT_FAN_PRICE = 60;
const DEFAULT_VIP_PRICE = 70;

export function getMinimumSubscriptionPrice(
  subscriptions: Subscriptions,
  level: PopStoreSubscriptionLevel,
): number {
  const getPrice = (subscriptionLevel: PopStoreSubscriptionLevel): number => {
    const subscription = subscriptions?.[subscriptionLevel];
    return subscription &&
      subscription.subscription_configuration_status ===
        PopStoreSubscriptionStatus.Active
      ? (subscription.creator_subscription.current_price
          ?.price_without_precision ?? 0)
      : 0;
  };

  const subscriberPrice =
    getPrice(PopStoreSubscriptionLevel.SUBSCRIBER) || DEFAULT_SUBSCRIBER_PRICE;
  const fanPrice = Math.max(subscriberPrice + 10, DEFAULT_FAN_PRICE);
  const vipPrice = Math.max(
    getPrice(PopStoreSubscriptionLevel.FAN) + 10,
    DEFAULT_VIP_PRICE,
  );

  switch (level) {
    case PopStoreSubscriptionLevel.SUBSCRIBER:
      return DEFAULT_SUBSCRIBER_PRICE / 100;
    case PopStoreSubscriptionLevel.FAN:
      return fanPrice / 100;
    case PopStoreSubscriptionLevel.VIP:
      return vipPrice / 100;
    default:
      throw new Error('Invalid subscription level');
  }
}
